import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  UserIcon,
  PhoneIcon,
  EnvelopeIcon,
  ClockIcon,
  PencilIcon,
  ArrowLeftIcon,
  NoSymbolIcon,
  EyeIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PlayIcon,
  CodeBracketIcon
} from '@heroicons/react/24/outline';
import { leadService } from '../../services/leadService';
import { callService } from '../../services/callService';
import { campaignService } from '../../services/campaignService';
import { Lead, CallAttempt } from '../../types/lead';
import { Campaign } from '../../types/campaign';
import { formatLeadCarrier, formatLeadLineType } from '../../utils/leadLookupDisplay';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import Breadcrumb from '../../components/UI/Breadcrumb';
import Button from '../../components/UI/Button';
import StatusBadge from '../../components/UI/StatusBadge';
import Table, { Column } from '../../components/UI/Table';
import Modal from '../../components/UI/Modal';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/UI/Card';

const ViewLeadPage: React.FC = () => {
  const { campaignId, leadId } = useParams<{ campaignId: string; leadId: string }>();
  const navigate = useNavigate();
  const [lead, setLead] = useState<Lead | null>(null);
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [callAttempts, setCallAttempts] = useState<CallAttempt[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCall, setSelectedCall] = useState<CallAttempt | null>(null);
  const [showTranscript, setShowTranscript] = useState(false);
  const [showRecording, setShowRecording] = useState(false);
  const [showCallDetails, setShowCallDetails] = useState(false);

  useEffect(() => {
    if (leadId && campaignId) {
      fetchCampaign();
      fetchLead();
    }
  }, [leadId, campaignId]);

  const fetchCampaign = async () => {
    if (!campaignId) return;

    try {
      const campaignData = await campaignService.getCampaign(campaignId);
      setCampaign(campaignData);
    } catch (error) {
      console.error('Failed to fetch campaign:', error);
    }
  };

  const fetchLead = async () => {
    try {
      setLoading(true);
      const [leadData, callAttemptsData] = await Promise.all([
        leadService.getLead(leadId!),
        leadService.getCallAttempts(leadId!)
      ]);
      setLead(leadData);
      setCallAttempts(callAttemptsData);
    } catch (error: any) {
      console.error('Failed to fetch lead:', error);
      console.error('Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        response: error.response?.data,
        status: error.response?.status,
        leadId
      });
      // TODO: Show error toast
    } finally {
      setLoading(false);
    }
  };



  const formatPhoneNumber = (phone: string) => {
    if (!phone) return 'No phone';
    // Simple formatting for display
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }
    return phone;
  };

  const formatDuration = (started_at?: string, finished_at?: string) => {
    if (!started_at || !finished_at) return 'N/A';
    const duration = Math.round((new Date(finished_at).getTime() - new Date(started_at).getTime()) / 1000);
    const minutes = Math.floor(duration / 60);
    const seconds = duration % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleViewTranscript = (call: CallAttempt) => {
    setSelectedCall(call);
    setShowTranscript(true);
    void hydrateCallMedia(call);
  };

  const handleViewRecording = (call: CallAttempt) => {
    setSelectedCall(call);
    setShowRecording(true);
    void hydrateCallMedia(call);
  };

  const handleViewCallDetails = (call: CallAttempt) => {
    setSelectedCall(call);
    setShowCallDetails(true);
    void hydrateCallMedia(call);
  };

  const hydrateCallMedia = async (call: CallAttempt) => {
    try {
      const [transcript, recordingUrl] = await Promise.all([
        call.transcript ? Promise.resolve(call.transcript) : callService.getCallTranscript(call.id).catch(() => null),
        // Always fetch on click so backend can refresh from Telnyx for expired URLs.
        callService.getCallRecording(call.id).catch(() => call.recording_url || null),
      ]);

      if (!transcript && !recordingUrl) return;

      setSelectedCall((prev) => {
        if (!prev || prev.id !== call.id) return prev;
        return {
          ...prev,
          transcript: prev.transcript || transcript || undefined,
          recording_url: prev.recording_url || recordingUrl || undefined,
        };
      });
    } catch {
      // best-effort media hydration
    }
  };

  const handleMarkDoNotCall = async () => {
    if (!lead) return;

    try {
      await leadService.markDoNotCall(lead.id);
      await fetchLead(); // Refresh lead data
    } catch (error) {
      console.error('Failed to mark as do not call:', error);
      // TODO: Show error toast
    }
  };

  // Define table columns for call history
  const callHistoryColumns: Column<CallAttempt>[] = [
    {
      key: 'attempt',
      header: 'Attempt #',
      render: (call: CallAttempt) => {
        const index = callAttempts.findIndex(c => c.id === call.id);
        return (
          <span className="font-medium text-foreground">#{callAttempts.length - index}</span>
        );
      },
      width: '100px'
    },
    {
      key: 'status',
      header: 'Status',
      render: (call) => (
        <StatusBadge status={call.status} type="call" />
      ),
      width: '120px'
    },
    {
      key: 'amd_result',
      header: 'AMD',
      render: (call) => {
        if (!call.amd_result) {
          return <span className="text-sm text-muted-foreground">—</span>;
        }
        const amdColors: Record<string, string> = {
          human: 'bg-green-100 text-green-800',
          machine: 'bg-red-100 text-red-800',
          not_sure: 'bg-yellow-100 text-yellow-800',
          fax: 'bg-gray-100 text-gray-800',
        };
        return (
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${amdColors[call.amd_result] || 'bg-gray-100 text-gray-800'}`}>
            {call.amd_result === 'not_sure' ? 'Unsure' : call.amd_result.charAt(0).toUpperCase() + call.amd_result.slice(1)}
          </span>
        );
      },
      width: '80px'
    },
    {
      key: 'result',
      header: 'Result',
      render: (call) => {
        // Derive result from lead status
        let resultStatus = null;
        if (lead?.status) {
          // Map lead status to result status
          switch (lead.status) {
            case 'interested':
              resultStatus = 'interested';
              break;
            case 'not_interested':
              resultStatus = 'not_interested';
              break;
            case 'failed':
              resultStatus = 'failed';
              break;
            default:
              resultStatus = null;
          }
        }

        return resultStatus ? (
          <StatusBadge status={resultStatus} type="result" />
        ) : (
          <span className="text-sm text-muted-foreground">N/A</span>
        );
      },
      width: '140px'
    },
    {
      key: 'duration',
      header: 'Duration',
      render: (call) => (
        <span className="text-sm text-foreground">
          {formatDuration(call.started_at?.toString(), call.finished_at?.toString())}
        </span>
      ),
      width: '100px'
    },
    {
      key: 'date',
      header: 'Date & Time',
      render: (call) => (
        <div className="text-sm">
          <div className="font-medium text-foreground">
            {call.started_at ? new Date(call.started_at).toLocaleDateString() : 'Unknown'}
          </div>
          <div className="text-muted-foreground">
            {call.started_at ? new Date(call.started_at).toLocaleTimeString() : 'Unknown time'}
          </div>
        </div>
      ),
      width: '160px'
    },
    {
      key: 'cost',
      header: 'Cost',
      render: (call) => {
        const cost = typeof call.cost === 'number' ? call.cost : (call.cost ? parseFloat(call.cost) : NaN);
        return (
          <span className="text-sm text-foreground">
            {call.cost && !isNaN(cost) ? `$${cost.toFixed(4)}` : 'N/A'}
          </span>
        );
      },
      width: '80px'
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (call) => (
        <div className="flex space-x-2">
          {call.transcript && (
            <Button
              onClick={() => handleViewTranscript(call)}
              variant="ghost"
              size="sm"
              className="p-1"
              title="View Transcript"
            >
              <DocumentTextIcon className="h-4 w-4" />
            </Button>
          )}
          {call.recording_url && (
            <Button
              onClick={() => handleViewRecording(call)}
              variant="ghost"
              size="sm"
              className="p-1"
              title="View Recording"
            >
              <EyeIcon className="h-4 w-4" />
            </Button>
          )}
          {!call.recording_url && call.telnyx_call_control_id && (
            <Button
              onClick={() => handleViewRecording(call)}
              variant="ghost"
              size="sm"
              className="p-1"
              title="Fetch Recording"
            >
              <EyeIcon className="h-4 w-4" />
            </Button>
          )}
          {call.telnyx_call_control_id && (
            <Button
              onClick={() => handleViewCallDetails(call)}
              variant="ghost"
              size="sm"
              className="p-1"
              title="View Call Details"
            >
              <CodeBracketIcon className="h-4 w-4" />
            </Button>
          )}
          {!call.transcript && !call.recording_url && !call.telnyx_call_control_id && (
            <span className="text-xs text-muted-foreground">No data</span>
          )}
        </div>
      ),
      width: '160px'
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="text-center py-12">
        <h3 className="mt-2 text-sm font-semibold text-foreground">Lead not found</h3>
        <p className="mt-1 text-sm text-muted-foreground">The lead you're looking for doesn't exist.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <Breadcrumb
        items={[
          { label: 'Campaigns', href: '/campaigns' },
          { label: campaign?.name || 'Campaign', href: `/campaigns/${campaignId}` },
          { label: 'Leads', href: `/campaigns/${campaignId}/leads` },
          { label: lead.fullName }
        ]}
      />

      {/* Header */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                onClick={() => navigate(`/campaigns/${campaignId}/leads`)}
                variant="ghost"
                size="sm"
                className="text-sm"
              >
                <ArrowLeftIcon className="h-4 w-4 mr-1" />
                Back to Leads
              </Button>
              <div className="h-4 w-px bg-border" />
              <h1 className="text-2xl font-bold text-foreground">Lead Details</h1>
            </div>
            <div className="flex items-center space-x-3">
              {lead.status !== 'do_not_call' && (
                <Button
                  onClick={handleMarkDoNotCall}
                  variant="destructive"
                >
                  <NoSymbolIcon className="-ml-0.5 mr-2 h-4 w-4" />
                  Mark Do Not Call
                </Button>
              )}
              <Button
                to={`/campaigns/${campaignId}/leads/${leadId}/edit`}
                className="btn btn-primary"
                variant="primary"
              >
                <PencilIcon className="-ml-0.5 mr-2 h-4 w-4" />
                Edit Lead
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lead Details */}
      <Card>
        <CardContent className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4">
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                <UserIcon className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-foreground">{lead.fullName}</h2>
                <p className="text-sm text-muted-foreground">
                  {formatPhoneNumber(lead.phoneE164)}
                  {lead.email && ` • ${lead.email}`}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <StatusBadge status={lead.status} type="lead" />
              {lead.externalRef && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                  Ref: {lead.externalRef}
                </span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            {/* Contact Information */}
            <div>
              <h3 className="text-lg font-medium text-foreground mb-4">Contact Information</h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <PhoneIcon className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Phone</p>
                    <p className="font-medium">{formatPhoneNumber(lead.phoneE164)}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <PhoneIcon className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Line type</p>
                    <p className="font-medium">{formatLeadLineType(lead.lineType)}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <PhoneIcon className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Carrier</p>
                    <p className="font-medium">{formatLeadCarrier(lead.carrierName, lead.carrier)}</p>
                  </div>
                </div>
                {lead.email && (
                  <div className="flex items-center space-x-3">
                    <EnvelopeIcon className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Email</p>
                      <p className="font-medium">{lead.email}</p>
                    </div>
                  </div>
                )}
                {lead.companyName && (
                  <div className="flex items-center space-x-3">
                    <UserIcon className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Company</p>
                      <p className="font-medium">{lead.companyName}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Call Information */}
            <div>
              <h3 className="text-lg font-medium text-foreground mb-4">Call Information</h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <PhoneIcon className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Call Attempts</p>
                    <p className="font-medium">{lead.callAttemptsCount || 0}</p>
                  </div>
                </div>
                {lead.lastCallAt && (
                  <div className="flex items-center space-x-3">
                    <ClockIcon className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Last Call</p>
                      <p className="font-medium">{new Date(lead.lastCallAt).toLocaleString()}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Custom Fields */}
          {lead.custom && Object.keys(lead.custom).length > 0 && (
            <div className="mt-6 pt-6 border-t border-border">
              <h3 className="text-lg font-medium text-foreground mb-4">Custom Fields</h3>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {Object.entries(lead.custom).map(([key, value]) => (
                  <div key={key} className="space-y-1">
                    <span className="text-sm text-muted-foreground capitalize block">{key.replace(/_/g, ' ')}</span>
                    <span className="font-medium block">{String(value)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Timestamps */}
          <div className="mt-6 pt-6 border-t border-border">
            <h3 className="text-lg font-medium text-foreground mb-4">Timestamps</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1">
                <span className="text-sm text-muted-foreground block">Created</span>
                <span className="font-medium block">
                  {new Date(lead.createdAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
              </div>
              <div className="space-y-1">
                <span className="text-sm text-muted-foreground block">Last Updated</span>
                <span className="font-medium block">
                  {new Date(lead.updatedAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Call History */}
      <Card>
        <CardContent className="px-4 py-5 sm:p-6">
          <h3 className="text-lg font-medium text-foreground mb-4">Call History</h3>
          {callAttempts && callAttempts.length > 0 ? (
            <Table
              data={callAttempts}
              columns={callHistoryColumns}
              size="sm"
              emptyState={
                <div className="text-center py-8">
                  <PhoneIcon className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium text-foreground mb-2">No Call History</h3>
                  <p className="text-sm text-muted-foreground">
                    This lead hasn't been called yet.
                  </p>
                </div>
              }
            />
          ) : (
            <div className="text-center py-8">
              <PhoneIcon className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">No Call History</h3>
              <p className="text-sm text-muted-foreground">
                This lead hasn't been called yet.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Transcript Modal */}
      <Modal
        isOpen={showTranscript}
        onClose={() => setShowTranscript(false)}
        title={selectedCall ? `Call Transcript - Attempt #${callAttempts.findIndex(c => c.id === selectedCall.id) + 1}` : ''}
        size="lg"
      >
        {selectedCall && (
          <div>
            <div className="mb-4 space-y-4">
              {/* Call Details */}
              <div className="p-4 bg-muted rounded-lg">
                <h4 className="font-medium mb-3 text-foreground">Call Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Call ID:</span>
                    <span className="font-medium">{selectedCall.telnyx_call_control_id || selectedCall.id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Duration:</span>
                    <span className="font-medium">{formatDuration(selectedCall.started_at?.toString(), selectedCall.finished_at?.toString())}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Status:</span>
                    <StatusBadge status={selectedCall.status} type="call" />
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Interest:</span>
                    <span className="font-medium">
                      {lead?.status === 'interested' ? 'Interested' :
                        lead?.status === 'not_interested' ? 'Not Interested' : 'N/A'
                      }
                    </span>
                  </div>
                  {selectedCall.cost && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Cost:</span>
                      <span className="font-medium">
                        ${typeof selectedCall.cost === 'number' ? selectedCall.cost.toFixed(4) : parseFloat(selectedCall.cost).toFixed(4)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Started:</span>
                    <span className="font-medium">
                      {selectedCall.started_at ? new Date(selectedCall.started_at).toLocaleString() : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Call Outcome */}
              {(lead?.status === 'interested' || lead?.status === 'not_interested' || selectedCall.reason) && (
                <div className="p-4 bg-muted rounded-lg">
                  <h4 className="font-medium mb-3 text-foreground">Call Outcome</h4>
                  <div className="space-y-2 text-sm">
                    {(lead?.status === 'interested' || lead?.status === 'not_interested') && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Interested:</span>
                        <span className={`font-medium ${lead?.status === 'interested' ? 'text-success' : 'text-destructive'
                          }`}>
                          {lead?.status === 'interested' ? 'Yes' : 'No'}
                        </span>
                      </div>
                    )}
                    {selectedCall.reason && (
                      <div>
                        <span className="text-muted-foreground block mb-1">Reason:</span>
                        <p className="text-foreground bg-background p-2 rounded border">
                          {selectedCall.reason}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Captured Data */}
              {selectedCall.captured_params && Object.keys(selectedCall.captured_params).length > 0 && (
                <div className="p-6 bg-card border border-success/20 rounded-lg shadow-sm">
                  <div className="flex items-center gap-2 mb-4">
                    <CheckCircleIcon className="h-5 w-5 text-success" />
                    <h4 className="font-semibold text-card-foreground">Captured Information</h4>
                  </div>
                  <div className="bg-success/5 p-4 rounded-md border-l-4 border-success">
                    <div className="space-y-3">
                      {Object.entries(selectedCall.captured_params).map(([key, value]) => (
                        <div key={key} className="flex justify-between items-start">
                          <span className="font-medium text-muted-foreground capitalize text-sm">
                            {key.replace(/([A-Z])/g, ' $1').trim()}:
                          </span>
                          <span className="text-card-foreground font-medium text-sm text-right max-w-xs">{String(value)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Error Message */}
              {selectedCall.reason && (
                <div className="p-6 bg-card border border-destructive/20 rounded-lg shadow-sm">
                  <div className="flex items-center gap-2 mb-3">
                    <ExclamationTriangleIcon className="h-5 w-5 text-destructive" />
                    <h4 className="font-semibold text-card-foreground">Error Details</h4>
                  </div>
                  <div className="bg-destructive/5 p-4 rounded-md border-l-4 border-destructive">
                    <p className="text-sm text-card-foreground leading-relaxed">{selectedCall.reason}</p>
                  </div>
                </div>
              )}
            </div>

            <div className="bg-background border border-border rounded-lg p-4 max-h-96 overflow-y-auto">
              <h4 className="font-medium mb-2">Transcript:</h4>
              <div className="text-sm text-foreground whitespace-pre-wrap">
                {selectedCall.transcript || 'No transcript available'}
              </div>
            </div>

            <div className="space-y-3">
              <audio controls className="w-full h-10 rounded-md">
                <source src={selectedCall.recording_url} type="audio/mpeg" />
                <source src={selectedCall.recording_url} type="audio/wav" />
                <source src={selectedCall.recording_url} type="audio/mp3" />
                Your browser does not support the audio element.
              </audio>
              <div className="flex items-center justify-between pt-2 border-t border-border">
                <span className="text-sm text-muted-foreground">Available formats: MP3, WAV</span>
                <a
                  href={selectedCall.recording_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm text-primary hover:text-primary/80 font-medium"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                  Open in new tab
                </a>
              </div>
            </div>

            <div className="mt-4 flex justify-end">
              <Button
                onClick={() => setShowTranscript(false)}
                variant="outlined"
              >
                Close
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Call Details Modal */}
      <Modal
        isOpen={showCallDetails}
        onClose={() => setShowCallDetails(false)}
        title={selectedCall ? `Call Details - ${selectedCall.telnyx_call_control_id || selectedCall.id}` : ''}
        size="lg"
      >
        {selectedCall && (
          <div>
            <div className="mb-4">
              {/* Call Details */}
              <div className="p-4 bg-muted rounded-lg">
                <h4 className="font-medium mb-3 text-foreground">Call Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Call Control ID:</span>
                    <span className="font-medium text-xs break-all">{selectedCall.telnyx_call_control_id || selectedCall.id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Duration:</span>
                    <span className="font-medium">{formatDuration(selectedCall.started_at?.toString(), selectedCall.finished_at?.toString())}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Status:</span>
                    <StatusBadge status={selectedCall.status} type="call" />
                  </div>
                  {selectedCall.cost && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Cost:</span>
                      <span className="font-medium">
                        ${typeof selectedCall.cost === 'number' ? selectedCall.cost.toFixed(4) : parseFloat(selectedCall.cost).toFixed(4)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="p-6 bg-card border border-border rounded-lg shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <CodeBracketIcon className="h-5 w-5 text-primary" />
                <h4 className="font-semibold text-card-foreground">Call JSON Data</h4>
              </div>
              <div className="bg-background border border-border rounded-lg p-4 max-h-96 overflow-y-auto">
                <pre className="text-xs text-foreground whitespace-pre-wrap overflow-x-auto">
                  {JSON.stringify(selectedCall, null, 2)}
                </pre>
              </div>
            </div>

            <div className="mt-4 flex justify-end">
              <Button
                onClick={() => setShowCallDetails(false)}
                variant="outlined"
              >
                Close
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Recording Modal */}
      <Modal
        isOpen={showRecording}
        onClose={() => setShowRecording(false)}
        title={selectedCall ? `Call Recording - Attempt #${callAttempts.findIndex(c => c.id === selectedCall.id) + 1}` : ''}
        size="lg"
      >
        {selectedCall && (
          <div>
            <div className="mb-4">
              {/* Call Details */}
              <div className="p-4 bg-muted rounded-lg">
                <h4 className="font-medium mb-3 text-foreground">Call Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Call ID:</span>
                    <span className="font-medium">{selectedCall.telnyx_call_control_id || selectedCall.id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Duration:</span>
                    <span className="font-medium">{formatDuration(selectedCall.started_at?.toString(), selectedCall.finished_at?.toString())}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Status:</span>
                    <StatusBadge status={selectedCall.status} type="call" />
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Started:</span>
                    <span className="font-medium">
                      {selectedCall.started_at ? new Date(selectedCall.started_at).toLocaleString() : 'N/A'}
                    </span>
                  </div>
                  {selectedCall.cost && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Cost:</span>
                      <span className="font-medium">
                        ${typeof selectedCall.cost === 'number' ? selectedCall.cost.toFixed(4) : parseFloat(selectedCall.cost).toFixed(4)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="p-6 bg-card border border-border rounded-lg shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <PlayIcon className="h-5 w-5 text-primary" />
                <h4 className="font-semibold text-card-foreground">Call Recording</h4>
              </div>
              {selectedCall.recording_url ? (
                <div className="space-y-3">
                  <audio controls className="w-full h-10 rounded-md">
                    <source src={selectedCall.recording_url} type="audio/mpeg" />
                    <source src={selectedCall.recording_url} type="audio/wav" />
                    <source src={selectedCall.recording_url} type="audio/mp3" />
                    Your browser does not support the audio element.
                  </audio>
                  <div className="flex items-center justify-between pt-2 border-t border-border">
                    <span className="text-sm text-muted-foreground">Available formats: MP3, WAV</span>
                    <a
                      href={selectedCall.recording_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm text-primary hover:text-primary/80 font-medium"
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                      Open in new tab
                    </a>
                  </div>
                </div>
              ) : (
                <div className="bg-muted/50 p-4 rounded-md border border-dashed border-muted-foreground/30">
                  <p className="text-sm text-muted-foreground text-center">No recording available for this call</p>
                </div>
              )}
            </div>

            <div className="mt-4 flex justify-end">
              <Button
                onClick={() => setShowRecording(false)}
                variant="outlined"
              >
                Close
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ViewLeadPage;